/**
 * Shared media URL helpers (admin UI, and any client that imports this module).
 * Keeps Google Drive handling in one place: normalize → file id → thumbnail / uc / view links.
 */

import { API_BASE_URL } from '../config/constants';

/**
 * Normalize DigitalOcean Spaces CDN URLs so img/video requests hit the same virtual-hosted
 * shape the upload route returns (avoids 404 / blank media when the key is wrong).
 */
export function normalizeDigitalOceanSpacesCdnUrl(inputUrl) {
  const raw = String(inputUrl || '').trim();
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const url = new URL(raw);
    if (!/\.cdn\.digitaloceanspaces\.com$/i.test(url.hostname)) return raw;

    const bucket = 'smart-mirror';
    const hostParts = url.hostname.split('.');
    const region = hostParts[0];

    if (hostParts.length === 4 && hostParts[1] === 'cdn') {
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts[0] === bucket) {
        url.hostname = `${bucket}.${region}.cdn.digitaloceanspaces.com`;
        url.pathname = `/${pathParts.slice(1).join('/')}`;
      }
    }

    return url.toString();
  } catch {
    return raw;
  }
}

let _apiOrigin;
function getApiOrigin() {
  if (_apiOrigin !== undefined) return _apiOrigin;
  try {
    _apiOrigin = new URL(API_BASE_URL).origin;
  } catch {
    _apiOrigin = '';
  }
  return _apiOrigin;
}

function coerceHttpsOriginOnSecurePage(origin) {
  const o = String(origin || '').trim();
  if (!o || !o.startsWith('http://')) return o;
  if (typeof window === 'undefined') return o;
  try {
    if (window.location.protocol !== 'https:') return o;
  } catch {
    return o;
  }
  return `https://${o.slice(7)}`;
}

/**
 * Paths like /uploads/media/… are served from the API host (e.g. :4000), not the React dev server (:3000).
 * Without this, <img>/<video> in the admin app request the wrong origin and only the poster / broken media shows.
 */
export function toAbsoluteMediaUrlForBrowser(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (/^\/\//.test(u)) return `https:${u}`;
  if (u.startsWith('/')) {
    const origin = coerceHttpsOriginOnSecurePage(getApiOrigin());
    return origin ? `${origin}${u}` : u;
  }
  return u;
}

/**
 * On an https admin page, http:// media is blocked (mixed content). Prefer https for the same resource.
 */
export function coerceHttpsOnSecurePage(url) {
  const u = String(url || '').trim();
  if (!u || !u.startsWith('http://')) return u;
  if (typeof window === 'undefined') return u;
  try {
    if (window.location.protocol !== 'https:') return u;
  } catch {
    return u;
  }
  return `https://${u.slice(7)}`;
}

export function trimUrl(s) {
  return (s || '').trim();
}

/**
 * Full absolute URL for img/video preview. Prepends https:// when users omit the scheme
 * (common cause of "blank" previews). Leaves relative paths (/uploads/…) unchanged.
 */
export function normalizeMediaUrl(raw) {
  const t = trimUrl(raw);
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (t.startsWith('/')) return t;
  if (/[.]/.test(t)) return `https://${t}`;
  return t;
}

export function looksLikeHls(url) {
  const t = normalizeMediaUrl(url).toLowerCase();
  if (!t) return false;
  return t.includes('.m3u8');
}

export function looksLikeVideoFile(url) {
  if (!trimUrl(url)) return false;
  const t = normalizeMediaUrl(url);
  try {
    const path = new URL(t).pathname.toLowerCase();
    return /\.(mp4|webm|m3u8|mov|mkv)(\?.*)?$/i.test(path);
  } catch {
    const u = url.toLowerCase();
    return /\.(mp4|webm|m3u8|mov|mkv)(\?|$)/.test(u);
  }
}

/** File ID from a Drive share / open link (not already a /uc? direct link). */
export function googleDriveFileId(raw) {
  const t = normalizeMediaUrl(raw);
  if (!t || !t.includes('drive.google.com')) return null;
  if (t.includes('/uc?') && t.includes('export=')) return null;
  try {
    const u = new URL(t);
    const fromPath = u.pathname.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fromPath) return fromPath[1];
    const id = u.searchParams.get('id');
    if (id) return id;
  } catch {
    /* ignore */
  }
  return null;
}

/** Same as above but also resolves `id` from `uc?export=…&id=` links (saved after submit). */
export function googleDriveFileIdAny(raw) {
  const t = normalizeMediaUrl(raw);
  if (!t || !t.includes('drive.google.com')) return null;
  try {
    const u = new URL(t);
    const fromPath = u.pathname.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fromPath) return fromPath[1];
    const id = u.searchParams.get('id');
    if (id) return id;
  } catch {
    /* ignore */
  }
  return null;
}

export function driveEmbedPreviewUrl(fileId) {
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

/** Opens the file in the Drive web UI (new tab). */
export function driveViewPageUrl(fileId) {
  return `https://drive.google.com/file/d/${fileId}/view`;
}

export function driveThumbnailUrl(fileId, sz = 'w1200') {
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=${sz}`;
}

/**
 * Drive "open" / "view" links are HTML pages — img/video need direct file access.
 * We convert public file links to uc URLs. File must be shared as "Anyone with the link".
 */
export function resolveMediaUrlForEmbed(raw, mode) {
  const n = normalizeMediaUrl(raw);
  if (!n) return '';
  const id = googleDriveFileId(n);
  if (!id) return coerceHttpsOnSecurePage(toAbsoluteMediaUrlForBrowser(n));
  if (mode === 'video') {
    return `https://drive.google.com/uc?export=download&id=${id}`;
  }
  return `https://drive.google.com/uc?export=view&id=${id}`;
}

/**
 * URL for img elements and client image loads (Drive → thumbnail API; else normalized direct/embed URL).
 * Mirrors Android MediaUrlResolver.resolveUrlForGlideImage.
 */
export function resolveUrlForImageLoad(raw) {
  const t = trimUrl(raw);
  if (!t) return '';
  if (looksLikeVideoFile(t)) return coerceHttpsOnSecurePage(toAbsoluteMediaUrlForBrowser(normalizeMediaUrl(t)));
  const n = normalizeMediaUrl(t);
  const id = googleDriveFileIdAny(n);
  if (id) return driveThumbnailUrl(id, 'w1200');
  return resolveMediaUrlForEmbed(n, 'image');
}

/** Thumbnail src for list cards: Drive → smaller thumbnail; else [resolveUrlForImageLoad]. */
export function adminListThumbSrc(url) {
  if (!url || looksLikeVideoFile(url)) return '';
  const id = googleDriveFileIdAny(url);
  if (id) return driveThumbnailUrl(id, 'w400');
  return resolveUrlForImageLoad(url);
}

/** YouTube watch/share URLs are pages, not progressive streams — use embed iframe (matches Android WebView path). */
export function extractYouTubeId(raw) {
  const n = normalizeMediaUrl(raw);
  if (!n) return null;
  try {
    const u = new URL(n);
    const host = u.hostname.toLowerCase();
    if (host.includes('youtu.be')) {
      const seg = u.pathname.split('/').filter(Boolean);
      return seg[0] || null;
    }
    if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const parts = u.pathname.split('/').filter(Boolean);
      const embedIdx = parts.indexOf('embed');
      if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
      const shortsIdx = parts.indexOf('shorts');
      if (shortsIdx >= 0 && parts[shortsIdx + 1]) return parts[shortsIdx + 1];
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function isYouTubeUrl(raw) {
  return extractYouTubeId(raw) != null;
}

/** Embed URL for admin preview / inline playback (not a direct file URL). */
export function youtubeEmbedSrcFromUrl(raw, opts = {}) {
  const id = extractYouTubeId(raw);
  if (!id) return '';
  const params = new URLSearchParams({
    autoplay: opts.autoplay === false ? '0' : '1',
    mute: opts.mute === false ? '0' : '1',
    playsinline: '1',
    modestbranding: '1',
    rel: '0',
    controls: opts.controls === false ? '0' : '1',
    enablejsapi: '1',
  });
  if (opts.loop) {
    params.set('loop', '1');
    params.set('playlist', id);
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}
